import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../index'

// Base selectors
export const selectCases = (state: RootState) => state.cases
export const selectCaseEntities = (state: RootState) => state.cases.entities
export const selectCaseIds = (state: RootState) => state.cases.ids
export const selectSelectedCaseId = (state: RootState) => state.cases.selectedCaseId
export const selectCasesLoading = (state: RootState) => state.cases.loading

// Get all cases as array
export const selectAllCases = createSelector(
  [selectCaseEntities, selectCaseIds],
  (entities, ids) => ids.map(id => entities[id])
)

// Get case by ID
export const selectCaseById = (caseId: string) => (state: RootState) => {
  const caseData = state.cases.entities[caseId]
  return caseData
}

// Get selected case
export const selectSelectedCase = createSelector(
  [selectCaseEntities, selectSelectedCaseId],
  (entities, selectedId) => selectedId ? entities[selectedId] : null
)

// Filter cases by search query
export const selectFilteredCases = (query: string) => createSelector(
  [selectAllCases],
  (cases) => {
    const lowerQuery = query.toLowerCase()
    return cases.filter(c => 
      c.name.toLowerCase().includes(lowerQuery) ||
      c.id.toLowerCase().includes(lowerQuery) ||
      c.type.toLowerCase().includes(lowerQuery)
    )
  }
)

// Get cases by status
export const selectCasesByStatus = (status: string) => createSelector(
  [selectAllCases],
  (cases) => cases.filter(c => c.status === status)
)

// Get case statistics
export const selectCaseStats = createSelector(
  [selectAllCases],
  (cases) => ({
    total: cases.length,
    pending: cases.filter(c => c.status === 'pending').length,
    processing: cases.filter(c => c.status === 'processing').length,
    complete: cases.filter(c => c.status === 'complete').length,
    error: cases.filter(c => c.status === 'error').length
  })
)

// Runtime selectors
export const selectCaseRuntimes = (state: RootState) => state.cases.runtimes

// Get runtime by case ID
export const selectRuntimeByCaseId = (caseId: string) => (state: RootState) =>
  state.cases.runtimes[caseId]

// Get runtime for selected case
export const selectSelectedCaseRuntime = createSelector(
  [selectCaseRuntimes, selectSelectedCaseId],
  (runtimes, selectedId) => selectedId ? runtimes[selectedId] : null
)

// Viewing document selector
export const selectViewingDocument = (state: RootState) => state.cases.viewingDocument
